/** Rechte des eigenen Nutzers für Nachrichtenaktionen in einem Kanal (über die Selektoren aus dem Store). */
import { isDm, isThread, type ChannelDto } from '@lagerfeuer/shared';
import { useMemo } from 'react';
import { useAppStore } from '../../store/app';
import { can, channelPermissions, useTimeoutExpiry } from '../../store/selectors';

export interface MessagePerms {
  canView: boolean;
  canReadHistory: boolean;
  canSend: boolean;
  canManage: boolean;
  canPin: boolean;
  canReact: boolean;
  canSuper: boolean;
  canAttach: boolean;
  canEmbed: boolean;
  canThread: boolean;
  canMentionEveryone: boolean;
  allowExternalEmojis: boolean;
  allowExternalStickers: boolean;
  /** Gespiegelter Kanal im Nur-Lesen-Modus oder pausiert (READONLY/PAUSED, Threads: Modus des Elternkanals). */
  readOnly: boolean;
  /** Spiegelung pausiert (Kanal oder ganze Guild). */
  bridgePaused: boolean;
  /** Slowmode gilt nicht (MANAGE_MESSAGES/MANAGE_CHANNELS). */
  bypassSlowmode: boolean;
}

/** Effektiver Brückenmodus: Threads erben den Modus des Elternkanals (wie die API, DECISIONS D25). */
export function effectiveBridgeMode(channel: ChannelDto | undefined, parent?: ChannelDto): ChannelDto['bridgeMode'] {
  if (!channel) return null;
  if (isThread(channel.type)) return parent?.bridgeMode ?? channel.bridgeMode;
  return channel.bridgeMode;
}

export function permsFor(perms: bigint, channel: ChannelDto | undefined, parent?: ChannelDto): MessagePerms {
  const mode = effectiveBridgeMode(channel, parent);
  const readOnly = mode === 'READONLY' || mode === 'PAUSED';
  const manage = can(perms, 'MANAGE_MESSAGES');
  return {
    canView: can(perms, 'VIEW_CHANNEL'),
    canReadHistory: can(perms, 'READ_MESSAGE_HISTORY'),
    canSend: can(perms, 'SEND_MESSAGES') && !readOnly,
    canManage: manage,
    canPin: manage || can(perms, 'PIN_MESSAGES'),
    canReact: can(perms, 'ADD_REACTIONS'),
    canSuper: can(perms, 'ADD_REACTIONS', 'USE_SUPER_REACTIONS'),
    canAttach: can(perms, 'ATTACH_FILES') && !readOnly,
    canEmbed: can(perms, 'EMBED_LINKS'),
    canThread: !!channel && !isThread(channel.type) && !isDm(channel.type) && channel.type !== 'VOICE' && can(perms, 'CREATE_THREADS'),
    canMentionEveryone: can(perms, 'MENTION_EVERYONE'),
    allowExternalEmojis: can(perms, 'USE_EXTERNAL_EMOJIS'),
    allowExternalStickers: can(perms, 'USE_EXTERNAL_STICKERS'),
    readOnly,
    bridgePaused: mode === 'PAUSED',
    bypassSlowmode: manage || can(perms, 'MANAGE_CHANNELS'),
  };
}

export function useMessagePerms(channelId: string): MessagePerms {
  const channel = useAppStore((s) => s.channels[channelId]);
  // Timeout-Ende → neu berechnen (Composer wieder frei).
  useTimeoutExpiry(channel?.serverId);
  const perms = useAppStore((s) => channelPermissions(s, channelId));
  const parent = useAppStore((s) => (channel?.parentId ? s.channels[channel.parentId] : undefined));
  return useMemo(() => permsFor(perms, channel, parent), [perms, channel, parent]);
}
